import { useState, useEffect } from "react";
import FRXLogo from "../../assets/frx-logo.jpg";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "@/redux/actions/authAction";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  capitalize,
  getAuthFromLocalStorage,
  getInitials,
  isUserPatient,
} from "@/utils/common.util";
import { getChatUnreadCount } from "@/redux/actions/chatActions";
import { RootState } from "@/redux/reducers";
import { Menu, X } from "lucide-react";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const headerMenu = [
    { label: "Home", route: "/home", enabled: true },
    { label: "Messages", route: "/messages", enabled: true },
    { label: "Users", route: "/users", enabled: !isUserPatient() },
    { label: "Tests", route: "/tests", enabled: true },
    { label: "Questionnaire", route: "/questionnaire", enabled: true },
  ];

  const getDefaultState = (currentPath: string) => {
    return (
      headerMenu
        .filter((menu) => menu.enabled)
        .findIndex(
          (item) =>
            currentPath.startsWith(item.route) ||
            item.route.startsWith("/" + currentPath.split("/")[1])
        ) || 0
    );
  };

  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const currentPath = window.location.pathname;
  const state = getDefaultState(currentPath);
  const { auth, user } = getAuthFromLocalStorage();
  const { unreadCount } = useSelector((state: RootState) => state.chat);

  const handleLogout = () => {
    dispatch(logout(() => navigate("/login")));
  };

  const handleMenuClick = (route: string) => {
    navigate(route);
    dispatch(getChatUnreadCount());
    setMenuOpen(false);
  };

  useEffect(() => {
    if (!auth) {
      navigate("/login");
    }
  }, [auth]);

  useEffect(() => {
    dispatch(getChatUnreadCount());
  }, []);

  return (
    <header className="flex justify-between items-center py-4 px-4 md:px-8 bg-white border-b relative">
      <div className="flex items-center">
        <a href="/">
          <img
            src={FRXLogo}
            alt="FRX Logo"
            className="w-10 h-auto mr-4 rounded"
          />
        </a>
        <nav className="space-x-0 hidden md:flex">
          {headerMenu
            .filter((menu) => menu.enabled)
            .map((item, index) => {
              if (item.label === "Messages") {
                return (
                  <div
                    key={`${item.label}-${index}`}
                    className={`relative text-gray-700 dark:text-gray-300 hover:bg-secondary hover:text-black dark:hover:text-white transition-colors p-2 px-6 rounded-lg cursor-pointer ${
                      state === index ? "bg-secondary text-black" : ""
                    }`}
                    onClick={() => handleMenuClick(item.route)}
                  >
                    {item.label}
                    {(unreadCount as number) > 0 && (
                      <span className="absolute top-0 right-0 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                        {unreadCount as number}
                      </span>
                    )}
                  </div>
                );
              } else {
                return (
                  <div
                    key={`${item.label}-${index}`}
                    className={`text-gray-700 dark:text-gray-300 hover:bg-secondary hover:text-black dark:hover:text-white transition-colors p-2 px-6 rounded-lg cursor-pointer ${
                      state === index ? "bg-secondary text-black" : ""
                    }`}
                    onClick={() => handleMenuClick(item.route)}
                  >
                    {item.label}
                  </div>
                );
              }
            })}
        </nav>
      </div>

      <button className="md:hidden" onClick={toggleMenu}>
        {menuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <nav
        className={`absolute md:relative top-16 left-0 w-full md:w-auto bg-white md:bg-transparent shadow-lg md:shadow-none p-4 md:p-0 flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4 transition-all duration-300 z-[1] ${
          menuOpen ? "block" : "hidden md:hidden"
        }`}
      >
        {headerMenu
          .filter((menu) => menu.enabled)
          .map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              className={`text-gray-700 dark:text-gray-300 hover:bg-secondary hover:text-black dark:hover:text-white transition-colors p-2 px-6 rounded-md cursor-pointer ${
                state === index ? "bg-secondary text-black" : ""
              }`}
              onClick={() => handleMenuClick(item.route)}
            >
              {item.label}
              {item.label === "Messages" && (unreadCount as number) > 0 && (
                <span className="ml-2 text-xs font-bold text-white bg-red-500 rounded-full px-2 py-1">
                  {unreadCount as number}
                </span>
              )}
            </div>
          ))}
        <div
          key="profile-info"
          className={`text-gray-700 dark:text-gray-300 transition-colors p-2 px-6 rounded-md border-t font-medium pt-4`}
        >
          {user?.name} ({capitalize(user?.role)})
        </div>
        <div
          key="logout"
          className={`text-gray-700 dark:text-gray-300 hover:bg-secondary hover:text-black dark:hover:text-white transition-colors p-2 px-6 rounded-md cursor-pointer`}
          onClick={() => handleLogout()}
        >
          Logout
        </div>
      </nav>

      <div className="hidden md:flex items-center space-x-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex items-center gap-1">
              <div className="flex flex-col mr-1">
                <p className="text-sm font-medium text-right">{user?.name}</p>
                <p className="text-xs text-gray-500 text-right">
                  {capitalize(user?.role)}
                </p>
              </div>
              <Avatar className="cursor-pointer w-10 h-10">
                <AvatarFallback className="bg-gray-500 text-white">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-40 mr-8">
            <DropdownMenuItem onClick={handleLogout}>Log Out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
